import { html, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { SecondaryNavCompactToggleEvent } from "@/components/shell/shell";
import { when } from "@/common/directives/when";
import EkoElement from "@/utilities/element";
import style from "./secondary-nav-group.css?inline";

type ToggleEventDetail = {
  collapsed: boolean;
  heading: string;
};

export type SecondaryNavGroupCollapseToggleEvent = CustomEvent<ToggleEventDetail>;

@customElement("eko-secondary-nav-group")
class NavigationGroup extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private handleToggle = () => {
    if (!this.heading) return;
    const collapsed = !this.collapsed;

    if (this.collapsed) {
      this.isHidden = false;
      window.requestAnimationFrame(() => (this.collapsed = collapsed));
    } else {
      this.collapsed = collapsed;
    }
    this.dispatchToggleEvent({ collapsed, heading: this.heading });
  };

  private handleTransitionEnd = (e: Event) => {
    if (e.target === e.currentTarget && (e as any).propertyName === "max-height") {
      this.isHidden = this.collapsed;
    }
  };

  @state() private isHidden = false;

  @property({ type: Boolean, reflect: true }) collapsed = false; // nav items are shown/hidden by clicking the group header
  @property({ type: Boolean, reflect: true }) compact = false; // only nav item icons are displayed
  @property() heading: string | undefined;

  setCompact = (event: SecondaryNavCompactToggleEvent) => {
    this.compact = event.detail.compact;
  };

  private dispatchToggleEvent(detail: ToggleEventDetail) {
    this.dispatchEvent(
      new CustomEvent("secondary-nav-group:collapse:toggle", {
        bubbles: true,
        detail,
      }) as SecondaryNavGroupCollapseToggleEvent
    );
  }

  connectedCallback(): void {
    super.connectedCallback();

    document.addEventListener("secondary-nav:compact:toggle", this.setCompact);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    document.removeEventListener("secondary-nav:compact:toggle", this.setCompact);
  }

  render() {
    return html`
      <div class="eko-secondary-nav-group">
        ${when(
          this.heading
            ? html`
                <div class="eko-secondary-nav-group__header">
                  <button
                    class="eko-secondary-nav-group__toggle"
                    aria-expanded=${!this.collapsed}
                    aria-controls="group-list"
                    @click=${this.handleToggle}>
                    <div class="eko-secondary-nav-group__icon">
                      <svg class="eko-secondary-nav-group__chevron" viewBox="0 0 10 6" fill="none">
                        <path d="M1 1L5 5L9 1" fill="currentColor" />
                        <path
                          d="M1 1L5 5L9 1H1Z"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5" />
                      </svg>
                    </div>
                    <h3 class="eko-secondary-nav-group__title">${this.heading}</h3>
                    <eko-visually-hidden>
                      ${this.collapsed ? "Expand" : "Collapse"} ${this.heading}
                    </eko-visually-hidden>
                  </button>
                </div>
              `
            : nothing
        )}
        <ul
          id="group-list"
          class="eko-secondary-nav-group__list"
          data-collapsed=${this.collapsed && !this.compact}
          ?hidden=${this.isHidden && !this.compact}
          @transitionend=${this.handleTransitionEnd}>
          <slot></slot>
        </ul>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-secondary-nav-group": NavigationGroup;
  }
}

export default NavigationGroup;
